import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { usePerformance } from "../context/PerformanceContext";
import PerformanceHourView from "../PerformanceHourDetail";
import PerformanceByStoreTable from "../PerformanceByStoreTable/PerformanceByStoreTable";
import PerformanceRadiusDetail from "../PerformanceRadiusDetail/PerformanceRadiusDetail";
import useSWR from "swr";
import { useStores } from "../../../context/storesContext";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import { useTerritories } from "../../../context/territoriesContext";
import { fetchCaLiveTurnoverByHour, fetcherGlobalCommKpiData, fetcherWithStoreKey } from "../../../utils/fetcher";
import { Range } from "../../../utils/range";
import { useLocation, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { formatDateToISO } from "../../../utils/dateUtils";
import { perfEventTrack } from "../../../utils/gtag";
import { getCALiveApiParams } from "../../CALiveFilterBar/CALiveFilterBar";
import { FormatGroup, TerritoryData } from "../../../types/commercial";
import { Breakpoint, MobileRayonRow, PerformanceApiGroup, PerformanceApiItem } from "../Performance.model";
import { formatKvalue } from "../../../utils/numberUtils";
import DeltaBadge from "../../DeltaBadge";
import MobileTerritoryDetail from "../TerritoryDetail/MobileTerritoryDetail";
import MobileTerritoriesList from "../TerritoryMobileList/MobileTerritoriesList";
import useBreakPoint from "../../../utils/useBreakPoint";
import useLandscape from "../../../utils/useLandscape";

interface PerformanceContentProps {
  importedDateTime?: string;
}

const withQuery = (path: string, params: string, extra?: Record<string, string>) => {
  const merged = new URLSearchParams(params);
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => merged.set(key, value));
  }
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
};

const isSameCalendarDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

const PerformanceContent: React.FC<PerformanceContentProps> = ({ importedDateTime }) => {
  const { view, performanceRange, selectedMagasin, analysisDate, comparativeDate, derivedComparativeDate, caLiveFilters, isCALive, setIsPerformanceLoading } = usePerformance();
  const [activeMobileTab, setActiveMobileTab] = useState<"territoires" | "rayons">("territoires");

  // State to track if a loading process has been initiated by a filter change.
  // This helps to keep the loading state true immediately after a filter change
  // until SWR's own loading states catch up.
  const [loadingInitiatedByFilterChange, setLoadingInitiatedByFilterChange] = useState(false);
  const { selectedStore } = useStores();
  const { isKpiExists } = useKpiVisibility();
  const { selectedTerritory } = useTerritories();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const hasMirror = !!selectedMagasin;

  const caLiveFilterParams = caLiveFilters ? getCALiveApiParams(caLiveFilters) : "";

  const byHour = (searchParams.get("byHour") ?? "SDV").replace(/\s+/g, '_');

  const breakPoint = useBreakPoint() as Breakpoint;
  const isPhone = breakPoint === "phone";
  const isLandscape = useLandscape();

  let range = Range.Direct;
  if (performanceRange === "S-1") {
    range = view === "horaire"  ? Range.LastWeek : Range.DirectLastWeek;
  } else if ((performanceRange === "N-1" || performanceRange === "CUSTOM") && (view === "horaire" || view === "magasin")) {
    range = Range.DirectLastYear;
  }

  useEffect(() => {
      let pageOrigin;
      if (location?.pathname.includes("/ca-live")) {
        pageOrigin = "live_turnover";
      } else if (location?.pathname.includes("/commercial")) {
        pageOrigin = "store";
      }
      let eventName;
      switch (view) {
        case "horaire":
          eventName = 'view_hourly_component';
          break;
        case "rayon":
          eventName = 'view_section_component';
          break;
        case "magasin":
          eventName = 'view_store_component';
          break;
        default:
          break;
      }
      if(!!eventName && !!pageOrigin) {
        perfEventTrack({
            eventName,
            pageOrigin,
        });
      }
  }, [view, location?.pathname]);

  /******************************/
  /*      perf-by-store         */
  /******************************/
  const { data: formatGroupData, isLoading: isKpiDataLoading, isValidating: isKpiDataValidating, error: kpiDataError } = useSWR(
    () => {
      if (view !== "magasin" && !isPhone) return null;

      if (isCALive) {
        return [withQuery("/ca-live/perf-by-store", caLiveFilterParams)];
      }

      if (!selectedTerritory?.key || !byHour || !isKpiExists("Commercial", "Turnover")) {
        return null;
      }

      const effectiveComparativeDate = performanceRange === "CUSTOM"
        ? comparativeDate
        : (analysisDate ? derivedComparativeDate : null);

      const hasCustomDates = !!analysisDate || !!effectiveComparativeDate;
      let url = `/commercial/perf-by-store?range=${hasCustomDates ? Range.Custom : range}`;
      if (analysisDate) url += `&analysisDate=${formatDateToISO(analysisDate)}`;
      if (effectiveComparativeDate) url += `&comparisonDate=${formatDateToISO(effectiveComparativeDate)}`;

      return [url, selectedTerritory.key as string, analysisDate, effectiveComparativeDate];
    },
    (args: any) => {
      if (isCALive) {
        return fetchCaLiveTurnoverByHour(args[0]);
      }
      return fetcherGlobalCommKpiData(args[0], args[1] as string).then((data: any) => data ? [data] : null);
    }
  );

  const isKpiPending = !!kpiDataError && !formatGroupData;

  /******************************/
  /*      turnoverByHour        */
  /******************************/
  // S-1 / N-1 / custom (date comparative) turnoverByHour
  const { data: customTurnoverByHourData, isLoading: isByHourDataLoading, isValidating: isByHourDataValidating} = useSWR(
    () => {
      if (!isKpiExists("Commercial", "TurnoverByHour") || view !== "horaire") {
        return null;
      }
      if (isCALive) {
        return [withQuery("/ca-live/turnoverByHour", caLiveFilterParams, { range: `${Range.DirectLastYear}` })] as const;
      }
      if (!selectedStore?.storeKey || !byHour) {
        return null;
      }

      const effectiveComparativeDate = performanceRange === "CUSTOM" ? comparativeDate : (analysisDate ? derivedComparativeDate : null);
      const comparisonBaseDate = analysisDate ?? new Date();
      const lastYearReferenceDate = new Date(comparisonBaseDate);
      // Backend computes "last year" with minusWeeks(52), so mirror that behavior on frontend.
      lastYearReferenceDate.setDate(lastYearReferenceDate.getDate() - (52 * 7));
      const isAnalysisToday = analysisDate ? isSameCalendarDay(analysisDate, new Date()) : true;
      const shouldUseDirectLastYear =
        isAnalysisToday &&
        !!effectiveComparativeDate &&
        isSameCalendarDay(effectiveComparativeDate, lastYearReferenceDate);
      const url = effectiveComparativeDate
        ? (shouldUseDirectLastYear
          ? `/commercial/turnoverByHour?range=${Range.DirectLastYear}&sector=${byHour}&storeType=${selectedStore.storeType.toUpperCase()}`
          : `/commercial/turnoverByHour?range=${Range.Custom}&date=${formatDateToISO(effectiveComparativeDate)}&sector=${byHour}&storeType=${selectedStore.storeType.toUpperCase()}`)
        : `/commercial/turnoverByHour?range=${range}&sector=${byHour}&storeType=${selectedStore.storeType.toUpperCase()}`;

      return [url, selectedStore.storeKey as string, effectiveComparativeDate] as const;
    },
    (args: any) => {
      if (isCALive) {
        return fetchCaLiveTurnoverByHour(args[0]);
      }
      return fetcherWithStoreKey(args[0], args[1]);
    }
  );
    
  // Direct turnoverByHour (ou date d'analyse)
  const {
    data: directTurnoverByHourData,
    isLoading: isLastByHourDataLoading,
    isValidating: isLastByHourDataValidating,
  } = useSWR(
    () => {
      if (!isKpiExists("Commercial", "TurnoverByHour") || view !== "horaire") {
        return null;
      }
      if (isCALive) {
        return [withQuery("/ca-live/turnoverByHour", caLiveFilterParams, { range: `${Range.Direct}` })] as const;
      }
      if (!selectedStore?.storeKey || !byHour) {
        return null;
      }
      const isAnalysisToday = analysisDate ? isSameCalendarDay(analysisDate, new Date()) : false;
      const url = analysisDate
        ? (isAnalysisToday
          ? `/commercial/turnoverByHour?range=${Range.Direct}&sector=${byHour}&storeType=${selectedStore.storeType.toUpperCase()}`
          : `/commercial/turnoverByHour?range=${Range.Custom}&date=${formatDateToISO(analysisDate)}&sector=${byHour}&storeType=${selectedStore.storeType.toUpperCase()}`)
        : `/commercial/turnoverByHour?range=${Range.Direct}&sector=${byHour}&storeType=${selectedStore.storeType.toUpperCase()}`;
      return [url, selectedStore.storeKey as string, analysisDate] as const;
    },
    (args: any) => {
      if (isCALive) {
        return fetchCaLiveTurnoverByHour(args[0]);
      }
      return fetcherWithStoreKey(args[0], args[1]);
    }
  );

    // lastWeek / N-1 / custom turnoverByHourComparison
    const {
        data: mirrorComparisonData,
        isLoading: isMirrorLoading,
        isValidating: isMirrorValidating,
    } = useSWR(
        () => {
            if (view !== "horaire" || !hasMirror || !selectedMagasin?.id) {
                return null;
            }
            const effectiveComparativeDate = performanceRange === "CUSTOM"
        ? comparativeDate
        : (analysisDate ? derivedComparativeDate : null);
            let url = `/commercial/turnoverByHourComparison?range=${range}&sector=${byHour}&storeType=${selectedStore?.storeType.toUpperCase()}`;
            if (analysisDate) {
                url += `&date=${formatDateToISO(analysisDate)}`;
            }
            if (effectiveComparativeDate) {
                url += `&comparativeDate=${formatDateToISO(effectiveComparativeDate)}`;
            }
            return [url, selectedMagasin.id, analysisDate, effectiveComparativeDate];
        },
        ([url, storeEan]) => fetcherWithStoreKey(url, storeEan as string)
    );

  /******************************/
  /*      turnoverBySector      */
  /******************************/

  const { data: sectorData, isLoading: isSectorDataLoading, isValidating: isSectorDataValidating, error: sectorDataError } = useSWR(
    () => {
        if (isCALive) {
          if (view !== "rayon" && !isPhone) return null;
          const url = withQuery("/ca-live/sector", caLiveFilterParams);
          return [url, "", "", ""];
        }

        if (!selectedStore?.storeKey || !isKpiExists("Commercial", "TurnoverBySector") || (view !== "rayon" && !isPhone)) return null;

        const effectiveComparativeDate = performanceRange === "CUSTOM"
          ? comparativeDate
          : (analysisDate ? derivedComparativeDate : null);

        const hasCustomDates = !!analysisDate || !!effectiveComparativeDate;
        const baseUrl = hasCustomDates
          ? `/commercial/sector?range=${Range.Custom}&storeType=${selectedStore.storeType.toUpperCase()}&sector=SDV`
          : `/commercial/sector?range=${Range.Direct}&storeType=${selectedStore.storeType.toUpperCase()}&sector=SDV`;

        let queryParams = "";
        if (analysisDate) queryParams += `&analysisDate=${formatDateToISO(analysisDate)}`;
        if (effectiveComparativeDate) queryParams += `&comparisonDate=${formatDateToISO(effectiveComparativeDate)}`;

        const url = `${baseUrl}${queryParams}`;
        const compareUrl = (!hasCustomDates && range === Range.DirectLastWeek) ? `/commercial/sector?range=${Range.DirectLastWeek}&storeType=${selectedStore.storeType.toUpperCase()}&sector=SDV` : null;

        return [url, compareUrl, selectedStore.storeKey as string, selectedStore.countryKey as string];
      },
    async ([url, compareUrl, storeEan, countryKey]) => {
      if (isCALive) {
        return fetchCaLiveTurnoverByHour(url);
      }
      const [data, comparisonData] = await Promise.all([
        url ? fetcherWithStoreKey(url, storeEan as string, countryKey as string) : Promise.resolve(undefined),
        compareUrl ? fetcherWithStoreKey(compareUrl, storeEan as string, countryKey as string) : Promise.resolve(undefined),
      ]);

      if(comparisonData) {
          const flattedComparisonData = comparisonData.flatMap((group: PerformanceApiGroup) => group.item) ?? [] ;
          data.flatMap((group: PerformanceApiGroup) => group.item).forEach((item: PerformanceApiItem) => {
              const comparisonItem = flattedComparisonData.find((compItem: PerformanceApiItem) => compItem.hypDepartmentKey === item.hypDepartmentKey && compItem.sector === item.sector);
              item.lastCa = comparisonItem?.ca ?? 0;
            })
      }
      return data;
    }
  );

  const isSectorPending = !!sectorDataError && !sectorData;

  const isHourLoading = isByHourDataLoading ||
    isLastByHourDataLoading ||
    isByHourDataValidating ||
    isLastByHourDataValidating ||
    (hasMirror && (isMirrorLoading || isMirrorValidating));

  const isRayonLoading = isSectorDataLoading || isSectorDataValidating || isSectorPending;

  const isMagasinLoading = isKpiDataLoading || isKpiDataValidating || isKpiPending;

  // Combined SWR loading state (true if any relevant SWR hook is loading or validating)
  const swrActiveLoading = useMemo(() => {
    switch (view) {
      case "horaire":
        return isHourLoading;
      case "rayon":
        return isRayonLoading;
      case "magasin":
        return isMagasinLoading;
      default:
        return false;
    }
  }, [view, isHourLoading, isRayonLoading, isMagasinLoading]);

  // Effect to mark loading as initiated when relevant filter dependencies change
  useEffect(() => {
    setLoadingInitiatedByFilterChange(true);
  }, [analysisDate, comparativeDate, performanceRange, selectedMagasin, view]);

  // Effect to manage the global loading state based on SWR and local initiation
  useEffect(() => {
    let isCurrentViewDataAvailable = false;
    switch (view) {
      case "horaire":
        // Data is considered available if both direct and custom data are not undefined,
        // and mirror data is not undefined if hasMirror is true.
        isCurrentViewDataAvailable = (directTurnoverByHourData !== undefined && customTurnoverByHourData !== undefined) && (!hasMirror || mirrorComparisonData !== undefined);
        break;
      case "rayon":
        isCurrentViewDataAvailable = sectorData !== undefined; // Data is "available" if SWR has returned something, even an empty array.
        break;
      case "magasin":
        isCurrentViewDataAvailable = formatGroupData !== undefined;
        break;
      default:
        isCurrentViewDataAvailable = false;
    }

    // The overall loading state is true if:
    // 1. SWR is actively loading/validating (swrActiveLoading is true).
    // 2. OR a loading process was just initiated by a filter change (loadingInitiatedByFilterChange is true).
    // 3. OR SWR is not actively loading/validating, but the data for the current view is still undefined.
    const overallLoading = swrActiveLoading || loadingInitiatedByFilterChange || (!swrActiveLoading && !isCurrentViewDataAvailable);
    setIsPerformanceLoading(overallLoading);

    // Reset the local initiation flag only when SWR reports no active loading AND data is available for the current view.
    // This ensures that `loadingInitiatedByFilterChange` doesn't keep `overallLoading` true indefinitely
    // if SWR finishes but data is still undefined. The `(!swrActiveLoading && !isCurrentViewDataAvailable)` part
    // of `overallLoading` will then take over to keep it true.
    if (!swrActiveLoading && isCurrentViewDataAvailable && loadingInitiatedByFilterChange) {
      setLoadingInitiatedByFilterChange(false);
    }
  }, [swrActiveLoading, loadingInitiatedByFilterChange, setIsPerformanceLoading, customTurnoverByHourData, directTurnoverByHourData, formatGroupData, hasMirror, mirrorComparisonData, sectorData, view]);

  function getMobileRayons(groups: PerformanceApiGroup[]): MobileRayonRow[] {
    if (!groups || groups.length < 2) {
      return [];
    }

    return groups
      .slice(1)
      .flatMap((group) => group.item)
      .filter((item) => item.hypDepartmentKey)
      .sort((a, b) => (b.ca ?? 0) - (a.ca ?? 0))
      .map((item, index) => ({
        id: `${item.sector}-${item.hypDepartmentKey}`,
        rank: index + 1,
        label: item.hypDepartmentDesc || item.hypDepartmentKey,
        sector: item.sector,
        ca: formatKvalue(item.ca),
        progress: item.rh,
      }));
  }

  const mobileRayons = useMemo(() => getMobileRayons(sectorData), [sectorData]);

  // Extract territory ID from params or fall back to matching the location pathname
  const territoryId = useMemo(() => {
    if (params.id) return params.id;
    const queryId = searchParams.get("territoryId");
    if (queryId) return queryId;
    const match = location.pathname.match(/\/performance\/territory\/([^/]+)/);
    return match ? match[1] : null;
  }, [params.id, searchParams, location.pathname]);

  // Find and map the target territory data for TerritoryDetail
  const selectedTerritoryData = useMemo(() => {
    if (!territoryId || !formatGroupData) return null;
    const allTerritories = formatGroupData.flatMap((group: FormatGroup) => group.territoryData || []);
    const found = allTerritories.find((td: TerritoryData) => td.id === territoryId);
    if (!found) return null;

    const apiStores = found.storesData || found.regionsData?.storesData || [];
    const mappedStores = apiStores.map((s: any) => ({
      id: s.storeKey || s.id,
      storeName: s.name,
      caN: s.kpis?.caN || 0,
      caN1: s.kpis?.caN1 || 0,
      clientsN: s.kpis?.clientsN || 0,
      progClients: s.kpis?.progClients || 0,
      progPrev: s.kpis?.progPrev || 0,
      quotePart: s.kpis?.quotePart || 0,
    }));

    return {
      id: found.id,
      territoryName: found.name,
      storeCount: mappedStores.length,
      overallTrend: found.kpis?.progCa || 0,
      caN: found.kpis?.caN || 0,
      progCa: found.kpis?.progCa || 0,
      ecartBudget: found.kpis?.progPrev || 100,
      stores: mappedStores,
    };
  }, [territoryId, formatGroupData]);


  if (isPhone && !isLandscape) {
    if (territoryId && selectedTerritoryData) {
      return (
        <MobileTerritoryDetail
          territory={selectedTerritoryData}
          onBack={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("territoryId");
            navigate(`?${newParams.toString()}`);
          }}
        />
      );
    }

    return (
      <MobilePerformanceContent>
        <MobilePerformanceHeaderBlock>
          <MobileViewSwitch>
            <MobileSwitchButton
              type="button"
              $active={activeMobileTab === "territoires"}
              onClick={() => setActiveMobileTab("territoires")}
            >
              <mrcl-icon icon="store" size="m" />
              CA Territoires
            </MobileSwitchButton>
            <MobileSwitchButton
              type="button"
              $active={activeMobileTab === "rayons"}
              onClick={() => setActiveMobileTab("rayons")}
            >
              <mrcl-icon icon="grid" size="m" />
              CA Rayons
            </MobileSwitchButton>
          </MobileViewSwitch>


        </MobilePerformanceHeaderBlock>

        {activeMobileTab === "territoires" ? (
          <MobileTerritoriesList
            formatGroupData={formatGroupData}
            isLoading={isMagasinLoading}
            />
        ) : (
          <MobileRayonList>
            {mobileRayons.map((rayon) => (
              <MobileRayonItem key={rayon.id}>
                <MobileRank>{rayon.rank}</MobileRank>
                <MobileRayonContent>
                  <MobileRayonName>
                    {rayon.label}
                    <MobileRayonSector>{rayon.sector}</MobileRayonSector>
                  </MobileRayonName>
                  <MobileRayonCa>{rayon.ca}</MobileRayonCa>
                </MobileRayonContent>
                <MobileRayonProgress>
                  <DeltaBadge value={rayon.progress} showSignWithIcon />
                </MobileRayonProgress>
              </MobileRayonItem>
            ))}
          </MobileRayonList>
        )}
      </MobilePerformanceContent>
    );
  }

  const renderContent = () => {
    switch (view) {
      case "horaire":
        return (
          <PerformanceHourView
            byHourToday={directTurnoverByHourData}
            byHourLastYear={customTurnoverByHourData}
            range={performanceRange}
            isLoading={isHourLoading}
            byHourComparison={mirrorComparisonData}
            isMirrorLoading={isMirrorLoading}
            selectedMagasin={selectedMagasin}
          />
        );

      case "rayon":
        return (
            <PerformanceRadiusDetail
              listValue={sectorData}
              range={performanceRange}
              isLoading={isRayonLoading}
            />
        );

      case "magasin":
        return (
          <PerformanceByStoreTable
            formatGroupData={formatGroupData}
            range={performanceRange}
            isLoading={isMagasinLoading}
            showKpis={isCALive}
            importedDateTime={importedDateTime}
          />
        );

      default:
        return null;
    }
  };

  return <Container>{renderContent()}</Container>;
};

export default PerformanceContent;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 932px) and (orientation: landscape) {
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;

    /* Ensure nested layout containers, grids, charts, and tables fit within bounds */
    table, div, section, article {
      max-width: 100%;
      box-sizing: border-box;
    }
  }
`;

const MobilePerformanceContent = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  width: 100%;
  background: #ffffff;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 932px) and (orientation: landscape) {
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    padding-bottom: 24px;
    height: auto;
  }
`;

const MobilePerformanceHeaderBlock = styled.div`
  background: #ffffff;
  border: 1px solid #ebebeb;
  box-sizing: border-box;
`;

const MobileViewSwitch = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  padding: 16px 12px;
  background: #ffffff;
`;

const MobileSwitchButton = styled.button<{ $active?: boolean }>`
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: ${({ $active }) => ($active ? "#0970e6" : "#f7f7f7")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#454545")};
  font-family: Ubuntu;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;


const MobileRayonList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const MobileRayonItem = styled.div`
  height: 79.5px;
  padding: 12px;
  border-bottom: 1px solid #ebebeb;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 70px;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  text-align: left;
`;

const MobileRank = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  color: #696969;
  font-family: Ubuntu;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
`;

const MobileRayonContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-self: stretch;
  text-align: left;
`;

const MobileRayonName = styled.span`
  color: #121212;
  font-family: Ubuntu;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`;

const MobileRayonSector = styled.span`
  color: #696969;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin-left: 6px;
`;

const MobileRayonCa = styled.span`
  color: #121212;
  font-family: Ubuntu;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
`;

const MobileRayonProgress = styled.div`
  justify-self: end;
  align-self: start;
  margin-top: 4px;

  & > span {
    min-height: 28px;
  }
`;
